import { FC, useState } from 'react';
import {
  Box,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Chip,
  IconButton,
  Typography,
  TextField,
  Button,
} from '@mui/material';
import AlternateEmail from '@mui/icons-material/AlternateEmail';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { EditableText, TwoFactorAuthenticationModal, useCustomerVerification, ResetModal } from '@components';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCustomer } from '@hooks/useCustomer';
import { setSelectedCustomer } from '@store/slices/customer';
import { addErrorToLog } from '@store/slices/error-log';
import LoadingButton from '@mui/lab/LoadingButton';
import { showToast } from '@store/slices/notification';
import { SubmitHandler } from 'react-hook-form';
import {
  useGetCustomerCouponsQuery,
  useLazyGetCustomersQuery,
  useUpdateCustomerAttributesMutation,
  useUpdateCustomerMutation,
  useResetFirebaseIdMutation,
  useVerifyUserMutation,
} from '@services';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import {
  Customer,
  CustomerAttributes,
  CustomerBlackListedData,
  FirebaseReset,
  JimboResponse,
  CustomerAttributeValues,
  RoleId,
} from '@models';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Fade from '@mui/material/Fade';
import { BlockOutlined, CalendarMonthOutlined } from '@mui/icons-material';
import moment, { Moment } from 'moment';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import EditIcon from '@mui/icons-material/Edit';
import jimboPalette from 'src/jimboTheme/palette';
import { formatPhone } from '@helpers';
import { InfoModal } from '@components';
import Loyaltylist from './Loyaltylist';
import { TerminateAccountModal } from '@components/TerminateAccountModal';

type CustomerNameSchema = Pick<Customer, 'first_name' | 'last_name'>;

type Props = {
  customer?: Customer;
  startEditing?: () => void;
  isBlackListedData?: CustomerBlackListedData;
  bigCCustomerAttributes?: CustomerAttributes[] | undefined;
  resetModal: (arg0: string) => void;
};

type CustomerCompanySchema = Pick<Customer, 'company'>;

type CustomerPhoneSchema = Pick<Customer, 'phone'>;

type CustomerNotesSchema = Pick<Customer, 'notes'>;

const dateOfBirthAttributeName = 'dob';

export const DetailView: FC<Props> = ({ isBlackListedData, bigCCustomerAttributes, resetModal }) => {
  const { customer, getAttribute, customerAttributes } = useCustomer();
  const [openInfo, setOpenInfo] = useState(false);
  const [infoContent, setInfoContent] = useState('');
  const { data: loyalty } = useGetCustomerCouponsQuery({ customerId: customer.id });
  const [updateCustomer] = useUpdateCustomerMutation();
  const [updateAttribute] = useUpdateCustomerAttributesMutation();
  const [updateFirebaseId, { isLoading: isFirebaseLoading }] = useResetFirebaseIdMutation();
  const [datePickerValue, setDatePickerValue] = useState<Moment | null>(null); //DesktopDatePicker value
  const activeUser = useAppSelector((state) => state.activeUser.value);
  const userAccessStatus = activeUser?.user?.roles.find((role: any) => role.moduleName === 'customer');
  const hasWriteAccess = userAccessStatus.role >= RoleId.Write;
  const [editDob, setEditDob] = useState(false);
  const [queryCustomer] = useLazyGetCustomersQuery();
  const hasResetFBAccess = userAccessStatus.role >= RoleId.ResetFirebaseRecord;
  const hasTerminateAccountWIthoutOTP = userAccessStatus.role >= RoleId.TerminateCustomerWithoutOTP;
  const [modalOpen, setModalOpen] = useState(false);
  const [verifyUser] = useVerifyUserMutation();
  const [facode, setFacode] = useState('');
  const [isErrorCode, setIsErrorCode] = useState(false);
  const { verified } = useCustomerVerification();
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [TerminateAccount2FA, setTerminateAccount2FA] = useState(false);
  const [ResetFirebase2FA, setResetFirebase2FA] = useState(false);
  const [openTerminateAccountModal, setOpenTerminateAccountModal] = useState(false);

  const dispatch = useAppDispatch();

  const isPhoneVerified = getAttribute('phonenumberverifieddate');
  const idVerifiedDate = getAttribute('IDVerifiedOn');
  const idVerificationAttempts = getAttribute('idVAttempts');
  const dateOfBirth = getAttribute(dateOfBirthAttributeName);

  const minDate = new Date(moment().subtract(101, 'years').toString());
  const maxDate = new Date(moment().subtract(18, 'years').toString());

  const dateDoB = new Date(dateOfBirth?.toString());
  const formattedDateOfBirth = `${dateDoB.getDate()}-${dateDoB.getMonth() + 1}-${dateDoB.getFullYear()}`;

  const verify2FA = async (evt: { preventDefault: () => void }) => {
    evt.preventDefault();

    try {
      setIsErrorCode(false);
      const value: any = await verifyUser({
        email: activeUser.user.email,
        code: facode,
      });

      if (value?.data?.code === 200 && ResetFirebase2FA) {
        setModalOpen(false);
        replaceCustomerFBRecord();
        setResetFirebase2FA(false);
      } else if (value?.data?.code === 200 && TerminateAccount2FA) {
        setModalOpen(false);
        setOpenTerminateAccountModal(true);
      } else {
        setIsErrorCode(true);
        setResetFirebase2FA(false);
      }
    } catch (err) {
      setIsErrorCode(true);
      setResetFirebase2FA(false);
      setTerminateAccount2FA(false);
      console.error('===verify2FA error===', err);
    }
  };

  const checkDOBConfirm = () => {
    if (idVerifiedDate?.length) {
      setIsResetModalOpen(true);
    } else {
      handleDobUpdate();
    }
  };

  const handleDobUpdate = async () => {
    const dobAttributeID = customerAttributes?.find((attribute: { name: string }) => attribute.name === dateOfBirthAttributeName)?.id;

    if (customer && datePickerValue && dobAttributeID && hasWriteAccess) {
      const date = new Date(datePickerValue.toString());
      const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(
        2,
        '0',
      )}`;

      if (minDate <= date && date <= maxDate) {
        const bigCAttributeID = bigCCustomerAttributes?.find((attr: CustomerAttributes) => attr.name === dateOfBirthAttributeName)?.id;
        const selectedAttributeID = customer.attributes.find(
          (attribute: { attribute_id: number | undefined }) => attribute.attribute_id === bigCAttributeID,
        )?.id;

        let updatedAttributes = customer.attributes.map((attribute: { id: any }) => {
          if (selectedAttributeID === attribute.id) {
            return { ...attribute, attribute_value: formattedDate };
          }

          return attribute;
        });

        // Update ID Verified property, again this is a bad practice to update only for store,
        // but we have to do it for now until we redo this file
        if (idVerifiedDate?.length) {
          const IDVerifiedOnId = customerAttributes.find((item: any) => item.name === 'IDVerifiedOn').id;

          updatedAttributes = updatedAttributes.filter((attr: CustomerAttributeValues) => attr.attribute_id !== IDVerifiedOnId);
        }

        const updatedCustomer = { ...customer, attributes: [...updatedAttributes] };

        updateAttribute({
          attribute_id: dobAttributeID,
          value: formattedDate,
          customer_id: customer.id,
          isDateOfBirthReset: true,
        })
          .unwrap()
          .then(() => {
            dispatch(
              showToast({
                message: `Successfully updated ${customer.first_name}'s DOB!`,
                type: 'success',
              }),
            );

            dispatch(setSelectedCustomer(updatedCustomer));
            setEditDob(false);
            setDatePickerValue(null);
          })
          .catch((error: { data: JimboResponse }) => {
            dispatch(addErrorToLog(error?.data));

            dispatch(
              showToast({
                message: `Failed to update ${customer.first_name}'s DOB!`,
                type: 'error',
              }),
            );

            setDatePickerValue(null);
          });
      } else {
        dispatch(
          showToast({
            message: `Failed to update ${customer.first_name}'s DOB!`,
            type: 'error',
          }),
        );
      }
    } else {
      dispatch(
        showToast({
          message: `Failed to update DOB!`,
          type: 'error',
        }),
      );
    }
  };

  /**
   *  Remove the IDVerifiedOn attribute on customer to update the state
   *  Its a terrible code practice however we need to rewrite the entire DetailView into a separate component as its
   * too big and complex to maintain
   */
  const removeIDVerifiedOn = (updatedCustomer: Partial<Customer>) => {
    if (idVerifiedDate?.length) {
      const propNames = Object.getOwnPropertyNames(updatedCustomer);

      if (propNames.some((val) => val === 'first_name' || val === 'last_name')) {
        const newCustomer = { ...customer };

        const IDVerifiedOnId = customerAttributes.find((item: any) => item.name === 'IDVerifiedOn').id;

        newCustomer.attributes = customer.attributes.filter((attr: CustomerAttributeValues) => attr.attribute_id !== IDVerifiedOnId);
        return newCustomer;
      }
    }

    return customer;
  };

  const updateCustomerProfile = (updatedCustomer: Partial<Customer>) => {
    const newCustomer = removeIDVerifiedOn(updatedCustomer);

    newCustomer &&
      updateCustomer({ ...newCustomer, ...updatedCustomer })
        .unwrap()
        .then((data) => {
          dispatch(
            showToast({
              message: `Successfully updated ${newCustomer.first_name}'s details!`,
              type: 'success',
            }),
          );

          // Concatenate instead of replacing the previous object because the API response doesn't contain addresses.
          data && dispatch(setSelectedCustomer({ ...newCustomer, ...data }));
        })
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          dispatch(
            showToast({
              message: `Failed to update ${newCustomer.first_name}'s details!`,
              type: 'error',
            }),
          );
        });
  };

  const onSubmit: SubmitHandler<Partial<Customer>> = async (updatedCustomer: Partial<Customer>) => {
    let valuesHaveChanged = false;

    for (const [key, value] of Object.entries(updatedCustomer)) {
      // Type is set to `any` because it would otherwise complain about the key being a string
      // whereas that isn't the correct type on the Customer interface. This is done to
      // allow for easily iterating over the keys without spelling them all out individually.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((customer as any)[key] !== value) {
        valuesHaveChanged = true;
      }
    }

    if (updatedCustomer.phone) {
      const unformatQueryPhone = queryCustomer({ searchType: 'phone', searchValue: updatedCustomer.phone, page: 1 });
      const formatQueryPhone = queryCustomer({ searchType: 'phone', searchValue: formatPhone(updatedCustomer.phone), page: 1 });

      Promise.allSettled([unformatQueryPhone, formatQueryPhone]).then((results) => {
        const unformatQueryResult = results
          .map((res: any) => (res?.value?.data ? res.value.data.data : null))
          .flat()
          .filter((item) => item);

        const otherUsers = unformatQueryResult.filter((item) => item.email !== customer.email);

        if (otherUsers.length) {
          // show this on the UI

          setInfoContent(
            `This phone number is already in use by another user Email: ${otherUsers[0].email} Name: ${otherUsers[0].first_name} ${otherUsers[0].last_name}.`,
          );

          setOpenInfo(true);
        } else {
          updateCustomerProfile(updatedCustomer);
        }
      });
    } else {
      if (valuesHaveChanged) {
        updateCustomerProfile(updatedCustomer);
      }
    }
  };

  const BlackListedLabel = () => {
    return (
      <Grid container alignItems="center">
        <Grid item xs={3} display={'contents'}>
          <Chip label="Blacklisted" color="error" icon={<BlockOutlined />}></Chip>
          <Tooltip
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 600 }}
            placement="right"
            title={
              <div>
                <div>Reason: {isBlackListedData?.reason}</div>
                <div>Until: {isBlackListedData?.until ? moment(isBlackListedData?.until).format('Do MMMM YYYY, h:mm:ss a') : 'N/A'}</div>
              </div>
            }
          >
            <IconButton>
              <InfoOutlinedIcon sx={{ color: 'black' }} />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    );
  };

  const replaceCustomerFBRecord = () => {
    try {
      const firebaseAttributeId = customerAttributes.find((item: any) => item.name === 'firebaseuid').id;
      const firebaseAttr: CustomerAttributeValues = customer.attributes.find((item: any) => item.attribute_id === firebaseAttributeId);

      const firebaseResetPayload: FirebaseReset = {
        firebaseUid: firebaseAttr.attribute_value,
        email: customer.email,
        firebaseAttributeId: firebaseAttributeId,
        bigCommerceId: customer.id,
      };

      updateFirebaseId(firebaseResetPayload)
        .unwrap()
        .then((data: JimboResponse<any>) => {
          dispatch(
            showToast({
              message: data.message ?? 'Successfully updated Firebase ID',
              type: 'success',
            }),
          );

          const updatedCustomer: Customer = {
            ...customer,
            attributes: customer.attributes.map((item: any) => {
              if (item.attribute_id === firebaseAttributeId) {
                return { ...item, attribute_value: data.data?.firebaseUid ?? '' };
              } else {
                return item;
              }
            }),
          };

          dispatch(setSelectedCustomer(updatedCustomer));
        })
        .catch((error: { data: JimboResponse }) => {
          dispatch(
            showToast({
              message: error.data?.message ?? 'Failed to update Firebase ID',
              type: 'error',
            }),
          );
        });
    } catch (err) {
      console.log(err);

      dispatch(
        showToast({
          message: `Failed to replace customer's FB record!`,
          type: 'error',
        }),
      );
    }
  };

  const notVerifiedToolTip = 'Verify Customer to edit';

  return customer ? (
    <>
      <ResetModal
        isOpen={isResetModalOpen}
        customer={customer}
        onClose={(isCancelled) => {
          if (!isCancelled) {
            handleDobUpdate();
          }

          setEditDob(false);

          setIsResetModalOpen(false);
        }}
        resetBtnLabel="Continue"
        bigCCustomerAttributes={customerAttributes}
        modalContent={{
          attributesToDelete: ['IDVerified', 'IDVerifiedOn', 'idVerificationData'],
          header: 'Change details',
          body: 'Please advise the customer that changing their name or date of birth will wipe the ID Verified status from their account, and they will need to verify again',
        }}
      />
      <InfoModal title="Customer Details" open={openInfo} content={infoContent} onClose={() => setOpenInfo(false)} />
      <Box data-testid="detail-view">
        <Grid container>
          <Grid item xs={12} sm={6} md={6} justifyContent="space-between">
            <List>
              <ListItem>{isBlackListedData?.isBlacklisted && BlackListedLabel()}</ListItem>
              <ListItem>
                <ListItemIcon>
                  <PersonOutlineOutlinedIcon />
                </ListItemIcon>
                <EditableText<CustomerNameSchema>
                  onSave={onSubmit}
                  useFormProps={{
                    resolver: yupResolver(
                      yup.object({
                        first_name: yup.string().required('First name is required'),
                        last_name: yup.string().required('Last name is required'),
                      }),
                    ),
                    defaultValues: {
                      first_name: customer.first_name,
                      last_name: customer.last_name,
                    },
                  }}
                  textProps={[
                    {
                      multiline: false,
                      size: 'small',
                      label: 'First Name',
                      placeholder: 'Enter your first name',
                    },
                    {
                      multiline: false,
                      size: 'small',
                      label: 'Last Name',
                      placeholder: 'Enter your last name',
                    },
                  ]}
                  disableEditing={!verified}
                  disabledEditingTooltip={notVerifiedToolTip}
                  isIDReset={idVerifiedDate?.length > 0}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <BusinessOutlinedIcon />
                </ListItemIcon>
                <EditableText<CustomerCompanySchema>
                  onSave={onSubmit}
                  useFormProps={{
                    resolver: yupResolver(
                      yup.object({
                        company: yup.string(),
                      }),
                    ),
                    defaultValues: {
                      company: customer.company,
                    },
                  }}
                  textProps={{
                    multiline: false,
                    size: 'small',
                    label: 'Company',
                    placeholder: 'Click to add Company',
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PhoneAndroidOutlinedIcon />
                </ListItemIcon>
                {customer.phone ? (
                  <ListItemText>{customer.phone}</ListItemText>
                ) : (
                  <EditableText<CustomerPhoneSchema>
                    onSave={onSubmit}
                    useFormProps={{
                      resolver: yupResolver(
                        yup.object({
                          phone: yup
                            .string()
                            .required('10 digit phone number required starting with 04')
                            .matches(/^04\d{8}$/g),
                        }),
                      ),
                      defaultValues: {
                        phone: '',
                      },
                    }}
                    textProps={{
                      multiline: false,
                      size: 'small',
                      label: 'Phone',
                      placeholder: 'Click to add Phone Number',
                    }}
                  />
                )}
              </ListItem>
              {customer.email && (
                <ListItem>
                  <ListItemIcon>
                    <AlternateEmail />
                  </ListItemIcon>
                  <ListItemText primary={customer.email} />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <CalendarMonthOutlined />
                </ListItemIcon>
                {dateOfBirth && !editDob ? (
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs="auto">
                      <ListItemText>{formattedDateOfBirth}</ListItemText>
                    </Grid>
                    <Grid item xs="auto">
                      <Tooltip title={!verified ? notVerifiedToolTip : ''} placement="top">
                        <span>
                          <IconButton onClick={() => setEditDob(true)} disabled={!verified}>
                            <EditIcon
                              fontSize="small"
                              data-testid="dob-edit-icon"
                              sx={{ cursor: 'pointer', '&:hover': { color: jimboPalette.primary.main } }}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                  </Grid>
                ) : (
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs="auto">
                        {
                          <DesktopDatePicker
                            disableFuture
                            value={datePickerValue}
                            label="Date of Birth"
                            inputFormat="D-M-YYYY"
                            minDate={moment().subtract(100, 'years')}
                            maxDate={moment().subtract(18, 'years')}
                            onChange={(newValue?) => {
                              setDatePickerValue(newValue);
                            }}
                            openTo="year"
                            data-testid="dob-date-picker"
                            renderInput={(params) => <TextField {...params} />}
                          />
                        }
                      </Grid>
                      <Grid item xs="auto">
                        <Button variant="contained" data-testid="dob-save-button" onClick={checkDOBConfirm} sx={{ width: '80px' }}>
                          Save
                        </Button>
                      </Grid>
                      <Grid item xs="auto">
                        <Button variant="outlined" data-testid="dob-cancel-button" onClick={() => setEditDob(false)} sx={{ width: '80px' }}>
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  </LocalizationProvider>
                )}
              </ListItem>
              <ListItem>
                <Chip
                  data-testid="phone-verified"
                  avatar={
                    <Avatar>
                      {isPhoneVerified ? <DoneIcon fontSize="small" color="info" /> : <CloseIcon fontSize="small" color="warning" />}
                    </Avatar>
                  }
                  color={isPhoneVerified ? 'success' : 'error'}
                  label={isPhoneVerified ? `Phone is verified` : 'Phone is not verified'}
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} sm={6} md={6} justifyContent="space-between">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" mt="2">
                  Customer ID Verification Status
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">ID Verification</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Chip
                  data-testid="view-id-verified"
                  avatar={
                    <Avatar>
                      {idVerifiedDate?.length > 0 ? (
                        <DoneIcon fontSize="small" color="info" />
                      ) : (
                        <CloseIcon fontSize="small" color="warning" />
                      )}
                    </Avatar>
                  }
                  color={idVerifiedDate?.length > 0 ? 'success' : 'error'}
                  label={idVerifiedDate?.length > 0 ? `ID Verified on ${idVerifiedDate}` : 'ID not verified'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">ID Verification Attempts</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" data-testid="id-verification-attempts">
                  {idVerificationAttempts ? `${idVerificationAttempts}` : null}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" mt="2">
                  Loyalty Program
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Loyaltylist loyaltylist={loyalty} customerId={customer.id} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" mt="2">
                  Activities
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Created</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">{new Date(customer?.date_created).toDateString()}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Last modified</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">{new Date(customer?.date_modified).toDateString()}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box padding={2}>
          <Typography sx={{ fontWeight: 'bold', marginBottom: 1 }}>Customer Notes</Typography>
          <EditableText<CustomerNotesSchema>
            onSave={onSubmit}
            useFormProps={{
              resolver: yupResolver(
                yup.object({
                  notes: yup.string(),
                }),
              ),
              defaultValues: {
                notes: customer.notes,
              },
            }}
            textFieldLabels={{
              notes: 'Add notes here',
            }}
            labelWhenNoDefaultValue="Click to add notes for customer"
            customWidth={500}
            customRow={4}
            changeGridLayout={12}
          />
        </Box>

        {window.FEATURE_FLAGS.REPLACE_FIREBASE_AUTH_7 && hasResetFBAccess && (
          <Grid container spacing={1} alignItems="center" p={2}>
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 'bold', marginBottom: 1 }}>Emergency use only (if there is no firebase record)</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <LoadingButton
                variant="contained"
                onClick={() => {
                  setModalOpen(true);
                  setFacode('');
                  setIsErrorCode(false);
                  setResetFirebase2FA(true);
                }}
                sx={{ width: '100%' }}
                loading={isFirebaseLoading}
                data-testid="reset-firebase-record-button"
              >
                Reset Firebase record
              </LoadingButton>
            </Grid>
          </Grid>
        )}
        {hasTerminateAccountWIthoutOTP && (
          <Grid container spacing={1} alignItems="center" p={2}>
            <Grid item xs={12} md={4}>
              <LoadingButton
                variant="contained"
                onClick={() => {
                  setModalOpen(true);
                  setFacode('');
                  setIsErrorCode(false);
                  setTerminateAccount2FA(true);
                }}
                sx={{ width: '100%' }}
                loading={TerminateAccount2FA}
                data-testid="terminate-account-without-otp-button"
              >
                Terminate Account Without OTP
              </LoadingButton>
            </Grid>
          </Grid>
        )}
        <TwoFactorAuthenticationModal
          isErrorCode={isErrorCode}
          modalTitle="Enter your 2FA code from Authenticator App"
          onSubmit={verify2FA}
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setTerminateAccount2FA(false);
          }}
          fieldValue={facode}
          onInputChange={(e) => setFacode(e.target.value)}
          errorDesc={'Invalid 2FA code. Please try again.'}
        />
        <TerminateAccountModal
          isOpen={openTerminateAccountModal}
          bigCCustomerAttributes={customerAttributes}
          customer={customer}
          onClose={() => {
            setOpenTerminateAccountModal(false);
            setTerminateAccount2FA(false);
          }}
          setModal={setOpenTerminateAccountModal}
        />
      </Box>
    </>
  ) : (
    <></>
  );
};
